import { LitElement, PropertyValueMap, PropertyValues, html } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import '@maersk-global/mds-components-core/mc-button'
import '@maersk-global/mds-components-core/mc-button-group'
import '@maersk-global/mds-components-core/mc-button-group-item'
import '@maersk-global/mds-components-core/mc-tag'
import '@maersk-global/mds-components-core/mc-input'
import { polarChartFormStyles } from '../style/polar-chart-styles'
import { _initialGMData, _initialSwellData, _initialVesselData, _initialWaveData, _initialWindData, _initialRollAngleData, _initialAccelerationForceData } from '../mock/polar-chart-mock-data'
import { PolarChartEvents } from '../helper'
import { FormField, ValidationRule } from '../types/polar-chart-form-types'
import { PolarFormData, validationRules } from '../utils/form-utils'
import { Coordinate } from 'ol/coordinate'
import { PolarPlotResponse } from '../types/polar-schema-types'
import { CoordinatesUtils } from '../../../src/utils/coordinates-utils'
import { ExtractedHeaderType } from '../utils/header-utils'
import { VoyagePlan } from '../../../src/models/vessel-planning-model'

@customElement('polar-chart-form')
export class PolarChartForm extends LitElement {
    static styles =   polarChartFormStyles;
    @property({ type: Boolean }) _isEditMode = false
    @property({ type: Array }) _waveData: FormField[] = [..._initialWaveData];
    @property({ type: Array }) _windData: FormField[] = [..._initialWindData];
    @property({ type: Array }) _swellData: FormField[] = [..._initialSwellData];
    @property({ type: Array }) _vesselData: FormField[] = [..._initialVesselData];
    @property({ type: Array }) _gmData: FormField[] = [..._initialGMData];
    @property({ type: Array }) _rollAngleData = [..._initialRollAngleData];
    @property({ type: Array }) _accelerationForceData = [..._initialAccelerationForceData];
    @property({ attribute: false }) polarRouteCapturedPosition: Coordinate | null = null;
    @property({ attribute: false }) polarPlotData: PolarPlotResponse | null = null;
    @property({ attribute: false}) responseHeaders: ExtractedHeaderType = null;
    @property({ attribute: false }) plan: VoyagePlan | null = null;
    @property({ type: Number }) companyLimit: number = 13;

    private _waveDataBackup = [];
    private _windDataBackup = [];
    private _swellDataBackup = [];
    private _vesselDataBackup = [];
    private _gmDataBackup = [];
    private _rollAngleDataBackup = [];
    private _accelerationForceDataBackup = [];
    private readonly _gmValidNumberMessage = 'Please enter a valid number (meters).';
    private readonly _fluidGmRequiredMessage = 'Fluid GM is required.';
    private readonly _solidGmRequiredMessage = 'Solid GM is required.';
    private readonly _solidGmMinRelationMessage = 'Solid GM must be greater than or equal to Fluid GM.';
    private readonly _solidGmMaxRelationMessage = 'Solid GM must not be more than 5.0 m greater than Fluid GM.';

    updated(changedProperties: PropertyValues) {

        // updated roll_angle_predicted if changed (after recalculation)
        if (changedProperties.has('polarPlotData') && this.polarPlotData) {
            const predictedField = this._rollAngleData.find(field => field.id === 'predicted_roll_angle');
            if (predictedField) {
                predictedField.value = this._sanitizeData('predicted_roll_angle', this.polarPlotData.roll_angle);
            }

            const accelerationField = this._accelerationForceData.find(field => field.id === 'predicted_acceleration_force_limit');
            if (accelerationField) {
                accelerationField.value = this._sanitizeData('predicted_acceleration_force_limit', this.polarPlotData.transverse_acceleration);
            }

            // reloading roll_angle_zone_limit from localStorage to get EDITED value
            const editedRollAngleZoneLimit = localStorage.getItem('polarPlotEditedRollAngle');

            const zoneLimitField = this._rollAngleData.find(field => field.id === 'roll_angle_zone_limit');
            if (zoneLimitField && editedRollAngleZoneLimit) {
                zoneLimitField.value = editedRollAngleZoneLimit;
                (zoneLimitField as any).error = '';
                this._validateField(zoneLimitField);
            }

            // reloading acceleration_force_limit from localStorage to get EDITED value
            const editedAccelerationLimit = localStorage.getItem('polarPlotEditedAccelerationLimit');

            const accelerationLimitReloadField = this._accelerationForceData.find(field => field.id === 'acceleration_force_limit');
            if (accelerationLimitReloadField && editedAccelerationLimit) {
                accelerationLimitReloadField.value = editedAccelerationLimit;
                (accelerationLimitReloadField as any).error = '';
                this._validateField(accelerationLimitReloadField);
            }
        }

        // only updates roll_angle_zone_limit if localStorage doesn't have an edited value
        // prevents overwriting user edits when plan prop updates
        if (changedProperties.has('plan')) {
            const editedRollAngleZoneLimit = localStorage.getItem('polarPlotEditedRollAngle');

            const zoneLimitField = this._rollAngleData.find(field => field.id === 'roll_angle_zone_limit');

            if (zoneLimitField && !editedRollAngleZoneLimit) {
                const initialValue = _initialRollAngleData.find(field => field.id === 'roll_angle_zone_limit')?.value;
                zoneLimitField.value = this._sanitizeData('roll_angle_zone_limit', this.plan?.rollAngle ?? initialValue);
            }

            const editedAccelerationLimit = localStorage.getItem('polarPlotEditedAccelerationLimit');
            const accelerationLimitField = this._accelerationForceData.find(field => field.id === 'acceleration_force_limit');
            const initialAccelerationLimit = _initialAccelerationForceData.find(field => field.id === 'acceleration_force_limit')?.value;

            if (accelerationLimitField && !editedAccelerationLimit) {
                accelerationLimitField.value = this._sanitizeData(
                    'acceleration_force_limit',
                    this.plan?.transverseAccelerationThreshold ?? initialAccelerationLimit,
                );
            }
        }
    }

    protected firstUpdated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
        // update polarplotData to _waveData, _windData, _swellData, _vesselData

        if(this.polarPlotData) {
            const { weather } = this.polarPlotData;
            this._waveData.find(field => field.id === 'wave_height')!.value = this._sanitizeData('wave_height', weather.wave_height);
            this._waveData.find(field => field.id === 'wave_direction')!.value = this._sanitizeData('wave_direction', weather.wave_direction);
            this._waveData.find(field => field.id === 'wave_period')!.value = this._sanitizeData('wave_period', weather.wave_period);

            this._windData.find(field => field.id === 'wind_speed')!.value = this._sanitizeData('wind_speed', weather.wind_speed);
            this._windData.find(field => field.id === 'wind_direction')!.value = this._sanitizeData('wind_direction', weather.wind_direction);

            this._swellData.find(field => field.id === 'swell_height')!.value = this._sanitizeData('swell_height', weather.swell_height);
            this._swellData.find(field => field.id === 'swell_direction')!.value =  this._sanitizeData('swell_direction', weather.swell_direction);
            this._swellData.find(field => field.id === 'swell_period')!.value = this._sanitizeData('swell_period', weather.swell_period);

            this._vesselData.find(field => field.id === 'vessel_current_speed')!.value = this._sanitizeData('vessel_current_speed', this.polarPlotData.speed_over_ground);
            this._vesselData.find(field => field.id === 'vessel_heading')!.value =  this._sanitizeData('vessel_heading', this.polarPlotData.course_over_ground);

            // get max speed from polar plot data
            let maxSpeed = -1;
            this.polarPlotData.maximum_speed_polygon.map(polarPoint => {
                if(polarPoint[0] > maxSpeed) {
                    maxSpeed = polarPoint[0];
                }
            });
            this._vesselData.find(field => field.id === 'vessel_max_speed')!.value = maxSpeed?.toFixed(1).toString();
            this._vesselData.find(field => field.id === 'vessel_position')!.value = CoordinatesUtils.formatCoordinates(this.polarRouteCapturedPosition ?? [this.polarPlotData.longitude, this.polarPlotData.latitude]);

            this._gmData.find(field => field.id === 'solid_gm')!.value = this._sanitizeData('solid_gm', this.polarPlotData.transverse_metacentric_height_solid);
            this._gmData.find(field => field.id === 'fluid_gm')!.value = this._sanitizeData('fluid_gm', this.polarPlotData.transverse_metacentric_height);

            this._rollAngleData.find(field => field.id === 'predicted_roll_angle')!.value = this._sanitizeData('predicted_roll_angle', this.polarPlotData.roll_angle);
            this._accelerationForceData.find(field => field.id === 'predicted_acceleration_force_limit')!.value = this._sanitizeData('predicted_acceleration_force_limit', this.polarPlotData.transverse_acceleration);

            // roll_angle_zone_limit, check for edited value first, then use plan value
            const editedRollAngleZoneLimit = localStorage.getItem('polarPlotEditedRollAngle');
            const zoneLimitField = this._rollAngleData.find(field => field.id === 'roll_angle_zone_limit')!;

            if (editedRollAngleZoneLimit) {
                // using stored EDITED value
                zoneLimitField.value = editedRollAngleZoneLimit;
                (zoneLimitField as any).error = '';
                this._validateField(zoneLimitField);
            } else if (this.plan?.rollAngle !== null && this.plan?.rollAngle !== undefined) {
                // using PLAN value on first load
                zoneLimitField.value = this._sanitizeData('roll_angle_zone_limit', this.plan.rollAngle);
            }

            const accelerationLimitField = this._accelerationForceData.find(field => field.id === 'acceleration_force_limit')!;
            const editedAccelerationLimit = localStorage.getItem('polarPlotEditedAccelerationLimit');
            const initialAccelerationLimit = _initialAccelerationForceData.find(field => field.id === 'acceleration_force_limit')?.value;

            if (editedAccelerationLimit) {
                // using stored EDITED value
                accelerationLimitField.value = editedAccelerationLimit;
                (accelerationLimitField as any).error = '';
                this._validateField(accelerationLimitField);
            } else {
                accelerationLimitField.value = this._sanitizeData(
                    'acceleration_force_limit',
                    this.plan?.transverseAccelerationThreshold ?? initialAccelerationLimit,
                );
            }

            this.requestUpdate();
        }
    }

    private _isKrugerModelAvailable(): boolean {
        const modelName = this.polarPlotData?.critical_roll_model;
        if (modelName) {
            return modelName === 'MLModel';
        }

        const cachedValue = localStorage.getItem('krugerModelAvailable');
        if (cachedValue === null) {
            return false;
        }

        try {
            return JSON.parse(cachedValue) === true;
        } catch {
            return false;
        }
    }

    _checkIfEdited = (field: FormField) => {
        const originalWeatherData = JSON.parse(localStorage.getItem('polarPlotDataWeather') || '[]');
        const polarPlotDataVessel = JSON.parse(localStorage.getItem('polarPlotDataVessel') || '[]');
        const combinedVesselObject = {...originalWeatherData, ...polarPlotDataVessel}
        const storedValue = this._sanitizeData(field.id, combinedVesselObject[field.id])
        const fieldValue = this._sanitizeData(field.id, field.value)
       return  !!(storedValue && fieldValue && storedValue !== fieldValue);
    }

    _sanitizeData = (fieldId: string, fieldValue?: string | number | null): string => {
        if(fieldValue !== undefined && fieldValue !== null){
            switch (fieldId){
                case 'wave_height':
                case 'wind_speed':
                case 'swell_height':
                case 'vessel_current_speed':
                case 'solid_gm':
                case 'fluid_gm':
                case 'predicted_roll_angle':
                case 'roll_angle_zone_limit':
                    return typeof fieldValue !== 'string' ? fieldValue?.toFixed(1).toString() : fieldValue
                case 'predicted_acceleration_force_limit':
                    return typeof fieldValue !== 'string' ? fieldValue?.toFixed(2).toString() : fieldValue
                case 'acceleration_force_limit':
                    return Number(fieldValue).toFixed(2)
                case 'wave_direction':
                case 'wave_period':
                case 'wind_direction':
                case 'swell_direction':
                case 'swell_period':
                case 'vessel_heading':
                    return Math.round(<number>fieldValue).toString()
                default:
                    fieldValue.toString()
            }
        }
        return ''
    }

    //change after connection to the backend
    _resetData() {
        this._waveData = [..._initialWaveData];
        this._windData = [..._initialWindData];
        this._swellData = [..._initialSwellData];
        this._vesselData = [..._initialVesselData];
        this._gmData = [..._initialGMData];
        this._rollAngleData = [..._initialRollAngleData];
        this._accelerationForceData = [..._initialAccelerationForceData];

        const originalWeatherData = localStorage.getItem('polarPlotDataWeather');
        if (originalWeatherData) {
            const weatherData = JSON.parse(originalWeatherData);
            this._waveData.find(field => field.id === 'wave_height')!.value = this._sanitizeData('wave_height', weatherData.wave_height);
            this._waveData.find(field => field.id === 'wave_direction')!.value = this._sanitizeData('wave_direction', weatherData.wave_direction);
            this._waveData.find(field => field.id === 'wave_period')!.value = this._sanitizeData('wave_period', weatherData.wave_period);

            this._windData.find(field => field.id === 'wind_speed')!.value = this._sanitizeData('wind_speed', weatherData.wind_speed);
            this._windData.find(field => field.id === 'wind_direction')!.value = this._sanitizeData('wind_direction', weatherData.wind_direction);

            this._swellData.find(field => field.id === 'swell_height')!.value = this._sanitizeData('swell_height', weatherData.swell_height);
            this._swellData.find(field => field.id === 'swell_direction')!.value = this._sanitizeData('swell_direction', weatherData.swell_direction);
            this._swellData.find(field => field.id === 'swell_period')!.value = this._sanitizeData('swell_period', weatherData.swell_period);
        }

        // resetting roll angle zone limit
        const initialRollAngleLimit = _initialRollAngleData.find(field => field.id === 'roll_angle_zone_limit')?.value;
        this._rollAngleData.find(field => field.id === 'roll_angle_zone_limit')!.value = this._sanitizeData('roll_angle_zone_limit', this.plan?.rollAngle ?? initialRollAngleLimit);
        const initialAccelerationLimit = _initialAccelerationForceData.find(field => field.id === 'acceleration_force_limit')?.value;
        this._accelerationForceData.find(field => field.id === 'acceleration_force_limit')!.value = this._sanitizeData(
            'acceleration_force_limit',
            this.plan?.transverseAccelerationThreshold ?? initialAccelerationLimit,
        );
        localStorage.removeItem('polarPlotEditedRollAngle');
        localStorage.removeItem('polarPlotEditedAccelerationLimit');

        if (this.polarPlotData?.roll_angle !== null && this.polarPlotData?.roll_angle !== undefined) {
            this._rollAngleData.find(field => field.id === 'predicted_roll_angle')!.value = this._sanitizeData('predicted_roll_angle', this.polarPlotData.roll_angle);
        }

        if (this.polarPlotData?.transverse_acceleration !== null && this.polarPlotData?.transverse_acceleration !== undefined) {
            this._accelerationForceData.find(field => field.id === 'predicted_acceleration_force_limit')!.value = this._sanitizeData('predicted_acceleration_force_limit', this.polarPlotData.transverse_acceleration);
        }

        const eventDetail = {
         waveData: this._waveData,
         windData: this._windData,
         swellData: this._swellData,
         vesselData: this._vesselData,
         isReset: true,
     };

     this.dispatchEvent(new CustomEvent(PolarChartEvents.PolarPlotReset, {
         detail: eventDetail,
         bubbles: true,
         composed: true
     }));
    }

    private _backupData() {
        this._waveDataBackup = JSON.parse(JSON.stringify(this._waveData));
        this._windDataBackup = JSON.parse(JSON.stringify(this._windData));
        this._swellDataBackup = JSON.parse(JSON.stringify(this._swellData));
        this._vesselDataBackup = JSON.parse(JSON.stringify(this._vesselData));
        this._gmDataBackup = JSON.parse(JSON.stringify(this._gmData));
        this._rollAngleDataBackup = JSON.parse(JSON.stringify(this._rollAngleData));
        this._accelerationForceDataBackup = JSON.parse(JSON.stringify(this._accelerationForceData));
    }

    private _restoreData() {
        this._waveData = JSON.parse(JSON.stringify(this._waveDataBackup));
        this._windData = JSON.parse(JSON.stringify(this._windDataBackup));
        this._swellData = JSON.parse(JSON.stringify(this._swellDataBackup));
        this._vesselData = JSON.parse(JSON.stringify(this._vesselDataBackup));
        this._gmData = JSON.parse(JSON.stringify(this._gmDataBackup));
        this._rollAngleData = JSON.parse(JSON.stringify(this._rollAngleDataBackup));
        this._accelerationForceData = JSON.parse(JSON.stringify(this._accelerationForceDataBackup));
    }

    _handleEditButtonClick() {
        if (!this._isEditMode) {
            this._backupData();
        }

        this._isEditMode = !this._isEditMode;
    }

    _handleCancelButtonClick() {
        this._restoreData();
        this._isEditMode = false;
    }

    _handleInputChange(event: Event, field: FormField) {
        const input = event.target as HTMLInputElement;
        let newValue = input.value.trim();
        const fieldId = field.id as string;

        // Update the appropriate data array based on field id
        this._waveData = this._waveData.map(f =>
            f.id === fieldId ? { ...f, value: newValue, edited: true } : f
        );
        this._windData = this._windData.map(f =>
            f.id === fieldId ? { ...f, value: newValue, edited: true } : f
        );
        this._swellData = this._swellData.map(f =>
            f.id === fieldId ? { ...f, value: newValue, edited: true } : f
        );
        this._vesselData = this._vesselData.map(f =>
            f.id === fieldId ? { ...f, value: newValue, edited: true } : f
        );
        this._gmData = this._gmData.map(f =>
            f.id === fieldId ? { ...f, value: newValue, edited: true } : f
        );
        this._rollAngleData = this._rollAngleData.map(f =>
            f.id === fieldId ? { ...f, value: newValue, edited: true } : f
        );
        this._accelerationForceData = this._accelerationForceData.map(f =>
            f.id === fieldId ? { ...f, value: newValue, edited: true } : f
        );

        if (newValue !== field.value) {
            this._updateField(field, newValue);
        }

        this._validateChangedField(field);
    }

    _handleBlur(event: Event, field: FormField) {
        const input = event.target as HTMLInputElement;
        let newValue = input.value.trim();
        const numericValue = parseFloat(newValue);

        if (newValue === "0" || (!isNaN(numericValue) && newValue !== "")) {
            newValue = this._sanitizeData(field.id, numericValue);
        }

        if (newValue !== field.value) {
            this._updateField(field, newValue);
        }

        this._validateChangedField(field);
    }

    _updateField(field: FormField, newValue: string) {
        field.value = newValue;
        field.edited = true;
        this.requestUpdate();
    }

     formatCoordinates(coordinate: string): string[] | string {
        const parts = coordinate.split(',');

        if (parts.length !== 2) {
            console.warn("Invalid coordinate format. Expected a single comma separating values.");
            return coordinate;
        }
        return parts
    }

    _readOnlyField(field: FormField) {
        const editedTag = this._checkIfEdited(field) ? html`<mc-tag fit="small" appearance="warning">Edited</mc-tag> ` : '';
        const displayValue = field.id === 'vessel_position' ? this.formatCoordinates(field.value) : field.value;
        return field.id === 'vessel_position' ? html `
                        <div class="read-only-container">
                            <div class="read-only-container-value">
                                ${displayValue[0].trim()}<br>
                                ${displayValue[1].trim()}
                            </div>
                            
                        </div>
        ` :  html` <div class="read-only-container">
                            <div class="read-only-container-value">
                                ${field.value}
                            </div>
                            <div class="read-only-container-unit">        
                                ${field.unit}    
                            </div>    
                           ${editedTag}
                        </div>`
    }

    _renderInputField(field: FormField) {
        const disableEdit = field.id === 'vessel_position' || field.id === 'vessel_max_speed' || field.id === 'predicted_roll_angle' || field.id === 'predicted_acceleration_force_limit';
        return html`
            <div class=${`input-container ${field.error ? 'input-container--error' : ''}`}>
                <div class="form-label">${field.label}:</div>
                <div>
                    ${(this._isEditMode && !disableEdit) ?
                        html`<mc-input
                        value=${field.value}
                        type=${field.id === "vessel_position" ? 'text' : 'number'}
                        suffix=${field.unit}
                        @blur=${(e: Event) => this._handleBlur(e, field)}
                        ?invalid=${!!field.error}
                        @input=${(e: Event) => this._handleInputChange(e, field)}
                        hiddenlabel
                        .readonly=${disableEdit ?? false}
                    >
                            ${field.error
                                ? html`<span slot="errormessage" style="width: 150px">${field.error}</span>`
                                : ''}
                        </mc-input>`: this._readOnlyField(field)}
                </div>
            </div>
        `
    }

    private _isGmField(fieldId: string): fieldId is 'solid_gm' | 'fluid_gm' {
        return fieldId === 'solid_gm' || fieldId === 'fluid_gm';
    }

    private _getFieldById(fieldId: string): FormField | undefined {
        return [
            ...this._waveData,
            ...this._windData,
            ...this._swellData,
            ...this._vesselData,
            ...this._gmData,
            ...this._rollAngleData,
            ...this._accelerationForceData,
        ].find((field) => field.id === fieldId);
    }

    private _validateChangedField(field: FormField): void {
        if (this._isGmField(field.id)) {
            this._validateGmFields();
            return;
        }

        this._validateField(field);
    }

    private _validateGmFields(): void {
        const fluidField = this._getFieldById('fluid_gm');
        const solidField = this._getFieldById('solid_gm');

        if (!fluidField || !solidField) {
            return;
        }

        const fluidRule = validationRules.fluid_gm;
        const solidRule = validationRules.solid_gm;
        const fluidValue = fluidField.value?.trim() ?? '';
        const solidValue = solidField.value?.trim() ?? '';

        fluidField.error = this._getGmFieldError(
            fluidValue,
            fluidRule,
            this._fluidGmRequiredMessage,
            fluidRule.errorMessage,
        );
        solidField.error = this._getGmFieldError(
            solidValue,
            solidRule,
            this._solidGmRequiredMessage,
            solidRule.errorMessage,
        );

        if (fluidField.error || solidField.error) {
            return;
        }

        const fluidNumericValue = Number(fluidValue);
        const solidNumericValue = Number(solidValue);

        if (solidNumericValue < fluidNumericValue) {
            solidField.error = this._solidGmMinRelationMessage;
            return;
        }

        if (solidNumericValue > fluidNumericValue + 5) {
            solidField.error = this._solidGmMaxRelationMessage;
        }
    }

    private _getGmFieldError(
        value: string,
        rule: ValidationRule,
        requiredMessage: string,
        rangeMessage: string,
    ): string {
        if (!value) {
            return requiredMessage;
        }

        if (!rule.regex.test(value)) {
            return this._gmValidNumberMessage;
        }

        const numericValue = Number(value);
        if (Number.isNaN(numericValue)) {
            return this._gmValidNumberMessage;
        }

        if ((rule.min !== undefined && numericValue < rule.min) ||
            (rule.max !== undefined && numericValue > rule.max)) {
            return rangeMessage;
        }

        return '';
    }

    private _validateField(field: FormField): void {
        if (this._isGmField(field.id)) {
            this._validateGmFields();
            return;
        }

        if ((field.id === 'predicted_roll_angle' || field.id === 'roll_angle_zone_limit' || field.id === 'predicted_acceleration_force_limit') &&
            !this._shouldShowRollAngleField(field)) {
            field.error = '';
            return;
        }

        const value = field.value?.trim();
        let errorMessage = '';
        const rule: ValidationRule = validationRules[field.id as keyof typeof validationRules];

        if (rule) {
            if (!rule.regex.test(value)) {
                errorMessage = rule.errorMessage;
            } else {
                const numericValue = parseFloat(value);
                let min = rule.min;
                let max = rule.max;
                let customErrorMessage = '';

                // special handling for roll_angle_zone_limit to use dynamic companyLimit
                if (field.id === 'roll_angle_zone_limit') {
                    min = 5;
                    max = this.companyLimit;
                    customErrorMessage = `Must be between ${min} and ${max} degrees.`;
                }

                if ((min !== undefined && numericValue < min) ||
                    (max !== undefined && numericValue > max)) {
                    errorMessage = customErrorMessage || rule.errorMessage;
                }
            }
        }

        field.error = errorMessage;
    }

    private _validateAllFields(): boolean {
        const allFields = [
            ...this._waveData,
            ...this._windData,
            ...this._swellData,
            ...this._vesselData,
            ...this._gmData,
            ...this._rollAngleData,
            ...this._accelerationForceData,
        ];

        let isValid = true;

        allFields.forEach((field: FormField) => {
            this._validateField(field);
            if (field.error) {
                isValid = false;
            }
        });

        return isValid;
    }

    private _hasBlockingErrors(): boolean {
        const visibleRollAngleFields: FormField[] = this._rollAngleData.filter((field) => this._shouldShowRollAngleField(field));
        const visibleFields: FormField[] = [
            ...this._waveData,
            ...this._windData,
            ...this._swellData,
            ...this._vesselData,
            ...this._gmData,
            ...visibleRollAngleFields,
            ...this._accelerationForceData,
        ];

        return visibleFields.some((field) => !!field.error);
    }

    _getSelectedOnListChange = (event: CustomEvent) => {
        const buttonGroup = event.currentTarget as HTMLElement & { value: string };

        const selectedValue = buttonGroup.value;
        this.dispatchEvent(new CustomEvent(PolarChartEvents.SelectedView, {
            detail: { view: selectedValue },
            bubbles: true, 
            composed: true 
        }));
    }

    //to change after conecting to the backend
    private async _handleCalculateButtonClick() {
        const isValid = this._validateAllFields();

        if (!isValid) {
            this.requestUpdate(); // Re-render to show errors
            return;
        }

        // Proceed if validation passes
        this._waveData = [...this._waveData];
        this._windData = [...this._windData];
        this._swellData = [...this._swellData];
        this._vesselData = [...this._vesselData];
        this._gmData = [...this._gmData];
        this._rollAngleData = [...this._rollAngleData];
        this._accelerationForceData = [...this._accelerationForceData];
        this._isEditMode = false;

        const eventDetail : PolarFormData = {
            waveData: this._waveData,
            windData: this._windData,
            swellData: this._swellData,
            vesselData: this._vesselData,
            gmData: this._gmData,
            rollAngleData: this._rollAngleData,
            accelerationForceData: this._accelerationForceData
        };

        if(!this.polarRouteCapturedPosition) {
            console.error('Polar route captured position is not set');
            return;
        }

        this.dispatchEvent(new CustomEvent(PolarChartEvents.PolarPlotRecalculate, {
            detail: eventDetail,
            bubbles: true,
            composed: true,
        }));
    }

    _renderEditButtons() {
        return this._isEditMode
            ? html`
                <mc-button
                    class="margin-left-button"
                    label="Cancel"
                    @click=${this._handleCancelButtonClick}
                    appearance="neutral"
                ></mc-button>
                <mc-button
                    class="margin-left-button"
                    appearance="primary"
                    @click=${this._handleCalculateButtonClick}
                    label="Calculate"
                    ?disabled=${this._hasBlockingErrors()}
                ></mc-button>`
            : html`
             <mc-tooltip>
                <mc-button
                    appearance="neutral"
                    @click=${this._handleEditButtonClick}
                    label="Edit"
                    slot="trigger"
                >             
                    <mc-icon icon="pencil"></mc-icon>    
                </mc-button>
                <span>Edit Details</span>
                 </mc-tooltip>`;
    }
    

    _shouldShowRollAngleSection(): boolean {
        return this._rollAngleData.some(field => this._shouldShowRollAngleField(field)) ||
            this._accelerationForceData.some(field => !!field.value?.trim());
    }

    // show only if available
    _shouldShowRollAngleField(field: FormField): boolean {
        if (field.id === 'predicted_roll_angle') {
            return this._isKrugerModelAvailable() && !!(field.value && field.value.trim() !== '');
        }
        if (field.id === 'roll_angle_zone_limit') {
            return this._isKrugerModelAvailable() && !!field.value?.trim();
        }
        return true;
    }

    _renderVersionInfo() {
        const { serviceVersion, modelType, modelVersion } = this.responseHeaders || {};

        if (!serviceVersion && !modelType) {
            return null;
        }

        const parts = [];

        if (serviceVersion) {
            parts.push(`Service: ${serviceVersion}`);
        }

        if (modelType) {
            parts.push(`${modelType} ${modelVersion}`);
        }

        return html`
            <div class="version-info">
                ${parts.map((part, index) => html`${index > 0 ? ', ' : ''}<span>${part}</span>`)}
            </div>
        `;
    }
    render() {
        return html`
            <div>
                <mc-card>
                    <div class="form-title">
                        Details
                        <div class="button-container">
                         <mc-tooltip>
                            <mc-button
                                appearance="neutral"
                                class="margin-right-button"
                                slot="trigger"
                            >
                                <mc-icon icon="arrow-anti-clockwise" @click=${this._resetData}></mc-icon>   
                            </mc-button>
                            <span>Reset Details</span>
                            </mc-tooltip>
                            ${this._renderEditButtons()}
                        </div>
                    </div>
                    <div class="form-container-section">
                        ${this._windData.map((field) =>
                            this._renderInputField(field),
                        )}
                    </div>
                    <div class="form-container-section">
                        ${this._waveData.map((field) =>
                            this._renderInputField(field),
                        )}
                    </div>
                    <div class="form-container-section">
                        ${this._swellData.map((field) =>
                            this._renderInputField(field),
                        )}
                    </div>
                    <div class="form-container-section">
                        ${this._vesselData.map((field) =>
                            this._renderInputField(field),
                        )}
                    </div>
                    ${this._shouldShowRollAngleSection() ? html`
                        <div class="form-container-section">
                            ${this._rollAngleData
                                .filter((field) => this._shouldShowRollAngleField(field))
                                .map((field) => this._renderInputField(field))
                            }
                            ${this._accelerationForceData.map((field) => this._renderInputField(field))}
                        </div>
                    ` : ''}
                    <div class="form-container-section">
                        ${this._gmData.map((field) =>
                            this._renderInputField(field),
                        )}
                    </div>
                    <div class="change-view-title">Change view:</div>
                    <mc-button-group
                        selectiontype="single"
                        @listchange="${this._getSelectedOnListChange}"
                        width="full-width"
                    >
                      <mc-button-group-item value="NorthUp" selected>
                            North Up
                        </mc-button-group-item>
                        <mc-button-group-item value="HeadUp" >
                            Head Up
                        </mc-button-group-item>

                    </mc-button-group>
  
                </mc-card>
                ${this._renderVersionInfo()}
            </div>
        `
    }
}
